// ===== TRAY TICKETS: WHICH RESIDENTS TO PRINT =====
//
// WinForms builds the tray-ticket PatientList for ONE facility in three steps
// (single facility: ucDietech_Report_Generator_TrayTicket.vb:770-882; multi-
// facility: the same steps per checked facility, .vb:486-596):
//
//   1. Specific Floor/Unit -> the facility's residents in the checked
//      floors/units (resident grid rows; floorUnitResidents.ts does the match).
//      Specific Resident -> the picked residents. All / Specific Set -> no list
//      (Nothing), which the generator reads as "everyone".
//   2. Special Need "Do Not Print Tray Ticket" -> drop those residents; when
//      step 1 left no list and the facility has any, build one of everyone else.
//   3. TT_DoNotPrintEmptyDiets -> drop residents with an empty diet order;
//      when there is still no list, build one of everyone with a diet.
//
// Each step stops the print with a message when nothing is left.

import { residentsInFloorUnits, type FloorUnitRow } from './floorUnitResidents';

export interface TrayTicketPoolPatient {
  pKey_Patient: number;
  floor: string | null;
  wing: string | null;
  dietOrder: string | null;
}

export interface TrayTicketFilterInput {
  printMode: string;
  /** The facility's resident grid rows (Fill_dtPatientGrid). */
  pool: readonly TrayTicketPoolPatient[];
  /** Checked floors/units on this facility's tab (printMode 'unit'). */
  unitSelection: readonly number[];
  /** Picked residents on this facility's tab (printMode 'resident'). */
  pickedPatients: readonly number[];
  useUnitMapping: boolean;
  units: readonly FloorUnitRow[];
  doNotPrintIds: readonly number[];
  doNotPrintEmptyDiets: boolean;
  /** Lower-case facility label, e.g. "residents". */
  residentPlural: string;
  /** Multi-facility: the facility name WinForms appends to each message
   *  (" for North Campus"). */
  facilityName?: string;
}

export type TrayTicketFilterResult =
  | { patients: number[] | null; error?: undefined }
  | { error: string };

export function filterTrayTicketPatients(input: TrayTicketFilterInput): TrayTicketFilterResult {
  const {
    printMode, pool, unitSelection, pickedPatients, useUnitMapping, units,
    doNotPrintIds, doNotPrintEmptyDiets, residentPlural, facilityName,
  } = input;
  const forFacility = facilityName ? ` for ${facilityName}` : '';

  let list: number[] | null = null;
  let unitPool: Set<number> | null = null;

  // Step 1 - Floor/Unit resolution (.vb:770-813 / :494-526). WinForms' multi-
  // facility loop matches on unit mapping even for a floor facility (where its
  // Select(...)(0) fails); each facility is matched on its own setting here.
  if (printMode === 'unit') {
    const matched = residentsInFloorUnits(pool, unitSelection, useUnitMapping, units);
    if (matched.length === 0) {
      return {
        error: `There are not any ${residentPlural} associated to the selected floors/units${forFacility}. Please widen selection.`,
      };
    }
    list = matched;
    unitPool = new Set(matched);
  } else if (printMode === 'resident') {
    list = pickedPatients.slice();
  }

  // Step 2 - Special Needs "Do Not Print Tray Ticket" (.vb:816-850 / :528-563).
  const doNotPrint = new Set(doNotPrintIds);
  if (doNotPrint.size > 0) {
    const kept = list == null
      ? pool.map((p) => p.pKey_Patient).filter((id) => !doNotPrint.has(id))
      : list.filter((id) => !doNotPrint.has(id));
    if (kept.length === 0) {
      return {
        error: `Nothing found to print. All ${residentPlural} selected have a Special Need marked as Do Not Print Tray Ticket${forFacility}.`,
      };
    }
    list = kept;
  }

  // Step 3 - empty diet orders (.vb:853-882 / :566-596).
  if (doNotPrintEmptyDiets) {
    const dietByKey = new Map<number, string>();
    for (const p of pool) {
      if (unitPool && !unitPool.has(p.pKey_Patient)) continue;
      dietByKey.set(p.pKey_Patient, p.dietOrder ?? '');
    }
    const kept = list == null
      ? pool.filter((p) => (p.dietOrder ?? '') !== '').map((p) => p.pKey_Patient)
      : list.filter((id) => (dietByKey.get(id) ?? '') !== '');
    if (kept.length === 0) {
      return {
        error: `Nothing found to print. All ${residentPlural} selected have an empty diet order${forFacility}. Review diet orders and reprint.`,
      };
    }
    list = kept;
  }

  return { patients: list };
}

/**
 * WinForms sends the several-facility settings (lstFKey_Facility_Multiple and the
 * per-facility lists) only when MORE than one facility is checked
 * (TrayTicket.vb:645-653 and :908-916); with just the current facility checked the
 * generator takes its single-facility path (TrayTickets.vb:91, :109). The tree
 * always keeps the current facility checked (trl_BeforeCheckNode), so one checked
 * facility is the current one: print it as a single facility, from its tab.
 *
 * Returns the form values with the tree cleared and SetList / UnitList /
 * PatientList as plain arrays for that facility; values with zero or several
 * checked facilities come back unchanged.
 */
export function collapseSingleCheckedFacility(v: Record<string, unknown>): Record<string, unknown> {
  const checked = (v.LstFacilityMultiple as number[] | undefined) ?? [];
  if (checked.length !== 1) return v;
  const fid = checked[0];
  const forFacility = (raw: unknown): number[] => {
    if (Array.isArray(raw)) return raw as number[];
    return ((raw ?? {}) as Record<number, number[]>)[fid] ?? [];
  };
  return {
    ...v,
    LstFacilityMultiple: [],
    SetList: forFacility(v.SetList),
    UnitList: forFacility(v.UnitList),
    PatientList: forFacility(v.PatientList),
  };
}
